import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { EnumService } from '../services/enum.service';
import { IdleSessionService } from '../services/idle-session.service';
import { PermissionService } from '../services/permission.service';
import { ConfirmHostComponent } from '../../shared/components/confirm-dialog/confirm-host.component';
import { LoggerService } from '../../shared/core/services/logger.service';
import { FooterComponent } from './footer/footer.component';
import { LockOverlayComponent } from './lock-overlay/lock-overlay.component';
import { NavbarComponent } from './navbar/navbar.component';
import { SidebarComponent } from './sidebar/sidebar.component';

@Component({
    selector: 'app-layout',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterOutlet, SidebarComponent, NavbarComponent, FooterComponent, ConfirmHostComponent, LockOverlayComponent],
    templateUrl: './layout.component.html',
    styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, OnDestroy {
    private authService = inject(AuthService);
    private permissionService = inject(PermissionService);
    private enumService = inject(EnumService);
    private idleSession = inject(IdleSessionService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly logger = inject(LoggerService);

    readonly sidebarCollapsed = signal(false);

    readonly sidebar = viewChild(SidebarComponent);

    ngOnInit(): void {
        // Le menu est deja declenche en amont via AuthService (login, tryRestoreSession).
        // L'intercepteur affiche deja le toast ; sans gestionnaire, l'echec remontait en erreur
        // non capturee et `user()` restait nul sans trace exploitable (audit R-30).
        this.authService
            .loadCurrentUser()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ error: this.tracerEchec('utilisateur') });
        this.permissionService
            .loadPermissions()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ error: this.tracerEchec('permissions') });
        this.enumService
            .load()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ error: this.tracerEchec('enumerations') });
        // Verrouillage sur inactivite — actif uniquement dans la zone authentifiee.
        this.idleSession.start();
    }

    private tracerEchec(quoi: string): (err: unknown) => void {
        return (err: unknown): void => this.logger.error(`Echec du chargement : ${quoi}`, err);
    }

    ngOnDestroy(): void {
        this.idleSession.stop();
    }

    onToggleSidebar(): void {
        if (window.innerWidth >= 1024) {
            this.sidebarCollapsed.set(!this.sidebarCollapsed());
        } else {
            const sidebar = this.sidebar();
            if (sidebar) {
                sidebar.mobileOpen.set(!sidebar.mobileOpen());
            }
        }
    }
}
